import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Send, Link, ToggleLeft, ToggleRight, ExternalLink, Copy, Check, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const UnderwritingShareSection = ({ applicationId, token }) => {
  const [shares, setShares] = useState([]);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [sending, setSending] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const fetchShares = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/applications/${applicationId}/underwriting-shares`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShares(data);
    } catch { /* silent */ }
  }, [applicationId, token]);

  useEffect(() => { fetchShares(); }, [fetchShares]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      const payload = { email: email.trim() };
      if (showMessage && message.trim()) {
        payload.message = message.trim();
      }
      await axios.post(`${API}/api/applications/${applicationId}/underwriting-share`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Share link sent to ${email.trim()}`);
      setEmail('');
      setMessage('');
      setShowMessage(false);
      fetchShares();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create share link');
    } finally {
      setSending(false);
    }
  };

  const handleToggle = async (linkId) => {
    try {
      const { data } = await axios.put(`${API}/api/underwriting-share/${linkId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(data.active ? 'Link activated' : 'Link deactivated');
      fetchShares();
    } catch {
      toast.error('Failed to toggle link');
    }
  };

  const copyLink = (shareToken) => {
    const url = `${window.location.origin}/shared/underwriting/${shareToken}`;
    navigator.clipboard.writeText(url);
    setCopiedId(shareToken);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <Card className="border-slate-200" data-testid="underwriting-share-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Link className="h-4 w-4 text-[#081120]" />
          Share Underwriting File
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={handleCreate} className="space-y-3" data-testid="share-email-form">
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="Enter lender or partner email..."
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 text-sm"
              required
              data-testid="share-email-input"
            />
            <Button type="submit" disabled={sending || !email.trim()} className="bg-[#081120] hover:bg-[#0f1d33] text-white gap-1.5 text-sm" data-testid="share-send-btn">
              <Send className="h-3.5 w-3.5" />
              {sending ? 'Sending...' : 'Share'}
            </Button>
          </div>

          {/* Message toggle */}
          <button
            type="button"
            onClick={() => setShowMessage(!showMessage)}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
            data-testid="share-message-toggle"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            <span>{showMessage ? 'Hide message' : 'Include a message'}</span>
            {showMessage ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>

          {/* Message textarea */}
          {showMessage && (
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a message for the recipient (e.g., context about the deal, what to review, next steps)..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-slate-400 placeholder:text-slate-400"
              data-testid="share-message-input"
            />
          )}
        </form>

        {shares.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Active Share Links</p>
            {shares.map((s) => (
              <div
                key={s.id}
                className={`flex items-center justify-between p-3 rounded-lg border text-sm transition-colors ${s.active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200/60 opacity-60'}`}
                data-testid={`share-link-${s.id}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Badge variant={s.active ? "default" : "secondary"} className={`text-[10px] shrink-0 ${s.active ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500'}`}>
                    {s.active ? 'Active' : 'Disabled'}
                  </Badge>
                  <span className="font-medium truncate">{s.email}</span>
                  <span className="text-xs text-slate-400 shrink-0">
                    {new Date(s.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 ml-2">
                  {s.active && (
                    <button
                      onClick={() => copyLink(s.token)}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                      title="Copy link"
                      data-testid={`copy-link-${s.id}`}
                    >
                      {copiedId === s.token ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  )}
                  {s.active && (
                    <a
                      href={`${window.location.origin}/shared/underwriting/${s.token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                      title="Preview link"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                  <button
                    onClick={() => handleToggle(s.id)}
                    className={`p-1.5 rounded transition-colors ${s.active ? 'hover:bg-red-50 text-slate-400 hover:text-red-500' : 'hover:bg-emerald-50 text-slate-400 hover:text-emerald-500'}`}
                    title={s.active ? 'Disable link' : 'Enable link'}
                    data-testid={`toggle-link-${s.id}`}
                  >
                    {s.active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UnderwritingShareSection;
